import { useCallback } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { EligibilitySnapshot, StatusLight } from '../../types';

export type VerificationMethod = 'portal' | 'phone' | '';

interface ManualOverrideFooterProps {
  snapshot: EligibilitySnapshot;
  verificationMethod: VerificationMethod;
  onToggleOverride: () => void;
  onOverrideChange: (field: 'status_light' | 'summary', value: string) => void;
  onMethodChange: (method: VerificationMethod) => void;
  onBlur?: () => void;
}

const STATUS_LIGHT_OPTIONS: { value: StatusLight; label: string }[] = [
  { value: 'GREEN', label: 'Verified' },
  { value: 'YELLOW', label: 'Needs Review' },
  { value: 'RED', label: 'Issue Detected' },
];

const METHOD_OPTIONS: { value: VerificationMethod; label: string }[] = [
  { value: 'portal', label: 'Payer portal (e.g., Availity)' },
  { value: 'phone', label: 'Phone call to payer' },
];

export function ManualOverrideFooter({
  snapshot,
  verificationMethod,
  onToggleOverride,
  onOverrideChange,
  onMethodChange,
  onBlur,
}: ManualOverrideFooterProps) {
  const handleStatusChange = useCallback(
    (event: SelectChangeEvent) => {
      onOverrideChange('status_light', event.target.value);
    },
    [onOverrideChange],
  );

  const handleSummaryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onOverrideChange('summary', event.target.value);
    },
    [onOverrideChange],
  );

  const handleMethodChange = useCallback(
    (event: SelectChangeEvent) => {
      onMethodChange(event.target.value as VerificationMethod);
    },
    [onMethodChange],
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1.5,
        borderStyle: 'dashed',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2">Manual Override</Typography>
        <Button size="small" variant="contained" onClick={onToggleOverride}>
          Done
        </Button>
      </Box>

      <Stack spacing={2}>
        <TextField
          label="Status"
          value={snapshot.status_light}
          onChange={(e) => handleStatusChange(e as SelectChangeEvent)}
          select
          size="small"
          fullWidth
        >
          {STATUS_LIGHT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="How did you verify?"
          value={verificationMethod}
          onChange={(e) => handleMethodChange(e as SelectChangeEvent)}
          select
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">
            <Typography color="text.secondary">Select method...</Typography>
          </MenuItem>
          {METHOD_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Summary"
          value={snapshot.summary}
          onChange={handleSummaryChange}
          onBlur={onBlur}
          size="small"
          fullWidth
          multiline
          rows={3}
          placeholder="Describe what you verified and the outcome..."
        />
      </Stack>
    </Paper>
  );
}
